import { CONSUMER_EVENT_ACTION_LABELS, type Consumer } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useConsumerEvents } from '../../../api/consumers';
import { formatDateTime } from '../format';

export interface ConsumerEventsDialogProps {
  open: boolean;
  consumer: Consumer;
  onClose: () => void;
}

/** Storico delle operazioni sul consumer: azione, autore, istante e dettaglio (DEC-006 §5). */
export function ConsumerEventsDialog({ open, consumer, onClose }: ConsumerEventsDialogProps) {
  const { data, isPending, isError, error } = useConsumerEvents(consumer.id);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="consumer-events-title">
      <DialogTitle id="consumer-events-title">Storico di {consumer.name}</DialogTitle>
      <DialogContent>
        {isPending && (
          <Stack direction="row" spacing={2} alignItems="center" role="status">
            <CircularProgress size={20} />
            <Typography>Caricamento dello storico…</Typography>
          </Stack>
        )}
        {isError && <Alert severity="error">{error.message}</Alert>}
        {data && (
          <List disablePadding aria-label="Eventi">
            {data.events.map((event) => (
              <ListItem key={event.id} divider disableGutters>
                <ListItemText
                  primary={`${CONSUMER_EVENT_ACTION_LABELS[event.action]} · ${event.actor}`}
                  secondary={`${formatDateTime(event.at)}${detailsLabel(event.details)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Etichette italiane dei campi registrati nei dettagli; le chiavi sconosciute restano tali. */
const DETAIL_LABELS: Record<string, string> = {
  name: 'Nome',
  authorizedCommittenti: 'Committenti',
  environment: 'Ambiente',
  product: 'Prodotto',
  url: 'Indirizzo',
  segretoPrecedenteValidoFino: 'Segreto precedente valido fino al',
  segretoPrecedenteIllegibile: 'Segreto precedente non leggibile',
  eventiScartati: 'Eventi in coda scartati',
};

/** Campi dei dettagli che sono istanti ISO: si mostrano come data e ora. */
const DATE_DETAILS = new Set(['segretoPrecedenteValidoFino']);

/** Valore leggibile di un dettaglio: data e ora, elenco, sì/no o testo. */
function detailValue(key: string, value: unknown): string {
  if (DATE_DETAILS.has(key) && typeof value === 'string') return formatDateTime(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'sì' : 'no';
  return String(value);
}

/** Dettaglio leggibile: `Etichetta: valore` per ogni campo registrato; i valori nulli non si mostrano. */
function detailsLabel(details: Record<string, unknown> | null): string {
  if (!details) return '';
  const parts = Object.entries(details)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${DETAIL_LABELS[key] ?? key}: ${detailValue(key, value)}`);
  return parts.length > 0 ? ` · ${parts.join(' · ')}` : '';
}
